import React, { useState, useCallback } from 'react';
import { Loading, InlineNotification } from 'carbon-components-react';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { useReportingAuthToken } from './hooks';
import { ReportsCrsViewer } from './ReportsCrsViewer';

/**
 * Base URL for the Reporting API.
 * Must be set via REACT_APP_REPORTING_API_BASE_URL environment variable.
 */
const REPORTING_API_BASE_URL = process.env.REACT_APP_REPORTING_API_BASE_URL;
if (!REPORTING_API_BASE_URL) {
  throw new Error(
    'REACT_APP_REPORTING_API_BASE_URL environment variable is not configured.'
  );
}

/**
 * ReportsCrs page component.
 *
 * WHAT: Main entry point for the CRS (Cartinuum Reporting Tool) module.
 *
 * WHY: This component handles auth token acquisition and renders the viewer
 * once the token is available. By using a keyed child component, we ensure
 * SDK hooks re-initialize if the token ever changes.
 *
 * HOW: Uses useReportingAuthToken hook for token management, then renders
 * ReportsCrsViewer with the token and baseUrl for SDK configuration.
 */
export const ReportsCrs: React.FC<{}> = () => {
  const {
    token,
    loading: loadingToken,
    error: tokenError,
    refresh: refreshToken,
    getToken,
    invalidateToken
  } = useReportingAuthToken();
  const [dismissedError, setDismissedError] = useState(false);

  // Check if the API base URL is configured
  const isMissingConfig = !REPORTING_API_BASE_URL;

  const handleDismissError = useCallback(() => {
    setDismissedError(true);
  }, []);

  const handleRetry = useCallback(() => {
    setDismissedError(false);
    refreshToken();
  }, [refreshToken]);

  return (
    <div id="reports-crs" className="report" data-testid="reports-crs">
      <FullHeaderLeftSideFullPanelLayout
        pageHeading={'Reporting'}
        pageDescription={
          'Generate and export custom management information reports'
        }
        enableHeaderGlobalBar={true}
      >
        <div className="report--wrapper">
          {/* Configuration error - API URL not set */}
          {isMissingConfig && (
            <InlineNotification
              kind="error"
              title="Configuration Error"
              subtitle="REACT_APP_REPORTING_API_BASE_URL environment variable is not configured."
              hideCloseButton
              style={{ marginBottom: '1rem' }}
            />
          )}

          {/* Token error display */}
          {!isMissingConfig && tokenError && !dismissedError && (
            <InlineNotification
              kind="error"
              title="Authentication Error"
              subtitle={tokenError}
              onCloseButtonClick={handleDismissError}
              actions={
                <button
                  type="button"
                  onClick={handleRetry}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  Retry
                </button>
              }
              style={{ marginBottom: '1rem' }}
            />
          )}

          {/* Loading state for token */}
          {!isMissingConfig && loadingToken && (
            <div className="loader--wrapper">
              <Loading description="Authenticating..." withOverlay={false} />
            </div>
          )}

          {/* Render viewer once token is available */}
          {!isMissingConfig && token && !loadingToken && (
            <ReportsCrsViewer
              key={token}
              getToken={getToken}
              onTokenInvalid={invalidateToken}
              baseUrl={REPORTING_API_BASE_URL}
            />
          )}

          {/* No token and not loading */}
          {!isMissingConfig && !token && !loadingToken && !tokenError && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Unable to authenticate. Please try again.</p>
            </div>
          )}
        </div>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

ReportsCrs.displayName = 'ReportsCrsModule';

export default React.memo(ReportsCrs);
